import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { ActivityAPIModel, CommentAPIModel } from 'src/app/models/api.model'
import { ActivityService } from 'src/app/services/activity/activity.service'
import { CommonService, formatDateToDDMMYYYY } from 'src/app/services/common/common.service'
import { DocumentService } from 'src/app/services/common/document.service'
import { InitializeService } from '../../../services/initialize/initialize.service'
import { Subscription } from 'rxjs'

@Component({
  selector: 'app-activity-details',
  templateUrl: './activity-details.component.html',
  styleUrls: ['./activity-details.component.scss']
})
export class ActivityDetailsComponent implements OnInit, OnDestroy {
  activityDetailsForm: FormGroup
  activity: ActivityAPIModel
  commentsToSave: CommentAPIModel[] = []
  private id: string
  readonly ACTIVITY_DASHBOARD_LINK = `/${GlobalConstants.ROUTING.ACTIVITY}/${GlobalConstants.ROUTING.DASHBOARD}`
  filesToBeUploaded: File[]
  hasFormChanged: boolean
  notify: boolean
  isActivityUnlocked: boolean = false
  selectStatusListFromState$: Subscription
  activityDetailsSubscription$: Subscription
  statusLabel: string

  constructor(
    private activityService: ActivityService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private cs: CommonService,
    private documentService: DocumentService,
    private initializeService: InitializeService
  ) {
  }

  ngOnInit() {
    this.buildActivityDetailsForm()
    this.route.params.subscribe((params) => {
      const id = params['id']
      this.id = id
      this.dispatchFetchActivityDetailsAction(id)
      this.getActivityDetailsFromStore()
    })
  }

  private buildActivityDetailsForm() {
    this.activityDetailsForm = this.formBuilder.group({
      displayId: [{ value: '', disabled: true }],
      name: [{ value: '', disabled: true }],
      type: [{ value: '', disabled: true }],
      createdDatetime: [{ value: '', disabled: true }],
      status: [{ value: '', disabled: true }, []],
      detailedStatus: [{ value: '', disabled: true }, []],
      description: [{ value: '', disabled: true }, []],
      statusUpdateFlag: [false, []]
    })
  }

  get f() {
    return this.activityDetailsForm.controls
  }

  private dispatchFetchActivityDetailsAction(id: any) {
    this.activityService.dispatchFetchActivityDetailsAction(id)
  }

  private getActivityDetailsFromStore() {
    this.activityDetailsSubscription$ = this.activityService.getActivityDetailsFromStore().subscribe((activity) => {
      if (activity) {
        this.activity = activity
        this.activityDetailsForm.patchValue(activity)
        this.activityDetailsForm.controls['createdDatetime'].setValue(formatDateToDDMMYYYY(activity.createdDatetime))
        this.subscribeFormForValueChanges(this.activityDetailsForm)

        if (this.activity.status.toLocaleLowerCase() !== 'completed') {
          this.isActivityUnlocked = true
        }

        this.selectStatusListFromState$ = this.initializeService
          .getStatusListByCodeFromState(this.activity.taskCategory, this.activity.taskSubCategory, GlobalConstants.MODULE.ACTIVITY_TRACKER, this.activity.taskCode)
          .subscribe((statusList) => {
            if (statusList) {
              console.log(statusList)
              const status = statusList.find(item => item.code === this.activity?.status)
              this.statusLabel = status ? status.label : ''
            }
          })
      }
    })
  }

  addComment(commentAPIModel: CommentAPIModel) {
    this.commentsToSave = [commentAPIModel]
  }

  getIfNotifySelected(notify) {
    this.notify = notify || false
  }

  updateActivity() {
    if (this.activityDetailsForm.invalid) {
      this.activityDetailsForm.markAllAsTouched()
      return
    }

    const commentToBeSaved = this.commentsToSave && this.commentsToSave[0]?.comment ? this.commentsToSave : []
    const notifyState = this.determineNotifyState(commentToBeSaved) || false

    this.activityService.dispatchUpdateActivityAction(
      {
        ...this.activityDetailsForm.value,
        statusUpdateFlag: false,
        comments: commentToBeSaved,
        name: this.activity.name,
        notify: notifyState
      },
      this.id,
      this.filesToBeUploaded,
      this.ACTIVITY_DASHBOARD_LINK
    )
  }

  downloadActivity() {
    const endPoint = ServiceUrlConstants.createDownloadExportPDFEndpoint('ACTIVITY', this.id)
    this.documentService.downloadDocument(endPoint)
  }

  handleFileInput(files: File[]) {
    this.filesToBeUploaded = files
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  private subscribeFormForValueChanges(formGroup: FormGroup) {
    const initialValue = formGroup.value
    formGroup.valueChanges.subscribe((value) => {
      this.hasFormChanged = Object.keys(initialValue).some(
        (key) => formGroup.value[key] != initialValue[key] && formGroup.dirty
      )
    })
  }

  determineNotifyState(commentToBeSaved: CommentAPIModel[]) {
    const hasCommented = commentToBeSaved?.length > 0
    const hasUploadedFiles = this.filesToBeUploaded !== null && this.filesToBeUploaded?.length > 0
    return ((this.hasFormChanged != null && this.hasFormChanged) || hasCommented || hasUploadedFiles) && this.notify
  }

  goToNewJoineeWorkflow() {
    const url = `/${GlobalConstants.ROUTING.FORM11_WORKFLOW}/${this.id}/${GlobalConstants.ROUTING.STATUS}/${this.activity.status}`
    this.cs.routeTo(url)
  }

  get canShowNewJoineeWorkflowButton(): boolean {
    const status = this.activity?.status
    return (
      this.activity?.name === 'New Joinee Workflow' &&
      status != 'COMPLETED' &&
      status != 'UPLOADED' &&
      status != 'AWAITING_FOR_INFORMATION' &&
      status != 'PENDING_SUPPORTING_DOC'
    )
  }

  ngOnDestroy(): void {
    this.activityDetailsSubscription$?.unsubscribe()
    this.selectStatusListFromState$?.unsubscribe()
    this.activityService.dispatchDeleteActivityDetailsAction()
  }
}
